import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../store/hook';
import { closeModal, openModal } from '../../store/features/modalSlice';
import HFbutton from '../button/HFbutton';
import ChannelService from '../../api/ChannelService';
import { Skeleton } from 'antd';
import { usePopups } from '../../hooks/usePopups';
import {
  EPopupFrequency,
  PopupCategory,
  PopupItem,
} from '../../constants/types/popup-type';

interface PopupProps {
  categoryId: PopupCategory;
}

const Popup = ({ categoryId }: PopupProps): null => {
  const dispatch = useAppDispatch();

  const {
    currentPopup,
    open,
    loading,
    closeCurrent,
    closeCurrentForToday,
    closeCurrentForDays,
    clickCurrent,
  } = usePopups(categoryId);

  useEffect(() => {
    if (!!currentPopup) {
      dispatch(
        openModal({
          body: (
            <PopupModal
              currentPopup={currentPopup}
              open={open}
              loading={loading}
              closeCurrent={closeCurrent}
              closeCurrentForToday={closeCurrentForToday}
              closeCurrentForDays={closeCurrentForDays}
              clickCurrent={clickCurrent}
            />
          ),
        }),
      );
    }
  }, [currentPopup]);

  return null;
};

export default Popup;

interface PopupModalProps {
  currentPopup: PopupItem | null;
  open: boolean;
  loading: boolean;
  closeCurrent: () => void;
  closeCurrentForToday: () => void;
  closeCurrentForDays: (days: number) => void;
  clickCurrent: () => Promise<void>;
}

const PopupModal = (props: PopupModalProps) => {
  const {
    currentPopup,
    open,
    loading,
    closeCurrent,
    closeCurrentForToday,
    closeCurrentForDays,
    clickCurrent,
  } = props;

  const dispatch = useAppDispatch();

  const [block, setBlock] = useState(false);
  const [clicking, setClicking] = useState(false);

  const keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      dispatch(closeModal());
    }
  };

  useEffect(() => {
    // mount 되면
    // 채널톡 숨기기, ESC 버튼 이벤트 추가
    ChannelService.hideChannelButton();
    window.addEventListener('keydown', keyHandler);

    return () => {
      // unmount 되면
      // 채널톡 보이기, ESC 버튼 이벤트 제거
      ChannelService.showChannelButton();
      window.removeEventListener('keydown', keyHandler);
    };
  }, []);

  const handleClickImage = useCallback(async () => {
    if (currentPopup.linkType === 'NONE' || clicking) return;

    setClicking(true);
    try {
      await clickCurrent();

      const target = currentPopup.linkType === 'NEW_TAB' ? '_blank' : '_self';
      window.open(currentPopup.linkUrl, target);
    } catch (error) {
      console.error('팝업 클릭 집계 실패', error);
    } finally {
      setClicking(false);
    }

    dispatch(closeModal());
  }, [currentPopup, clicking]);

  const handleClose = () => {
    closeCurrent();
    dispatch(closeModal());
  };

  const handleCloseCurrentForToday = () => {
    closeCurrentForToday();
    dispatch(closeModal());
  };

  const handleCloseCurrentForDays = (days: number) => () => {
    closeCurrentForDays(days);
    dispatch(closeModal());
  };

  if (loading || !currentPopup || !open) return null;

  const renderButtons = (
    frequency: keyof typeof EPopupFrequency,
    frequencyDays: number,
  ) => {
    switch (frequency) {
      case 'ALWAYS':
        return (
          <>
            <HFbutton onClick={handleClose}>닫기</HFbutton>
          </>
        );
      case 'ONCE':
        // INFO: 백오피스에서 팝업 생성 및 수정 시 ONCE 타입으로 생성, 수정할 수 없으므로 일단 null 반환.
        return null;
      case 'DAILY':
        return (
          <>
            <HFbutton
              type="primary-outlined"
              onClick={handleCloseCurrentForToday}
            >
              하루간 보지 않기
            </HFbutton>
            <HFbutton onClick={handleClose}>닫기</HFbutton>
          </>
        );
      case 'CUSTOM_DAYS':
        return (
          <>
            <HFbutton
              type="primary-outlined"
              onClick={handleCloseCurrentForDays(frequencyDays)}
            >
              {frequencyDays}일간 보지 않기
            </HFbutton>
            <HFbutton onClick={handleClose}>닫기</HFbutton>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex w-full max-w-[480px] flex-col gap-1 rounded bg-white p-1">
      {!block && (
        <Skeleton.Image active style={{ width: '100%', height: '300px' }} />
      )}
      <img
        className={`w-full ${currentPopup.linkType === 'NONE' ? '' : 'cursor-pointer'} object-cover ${block ? '' : 'hidden'}`}
        src={currentPopup.imagePcUrl}
        onClick={handleClickImage}
        onLoad={() => setBlock(true)}
      />
      <div className="flex gap-3 p-2">
        {renderButtons(currentPopup.frequency, currentPopup.frequencyDays)}
      </div>
    </div>
  );
};
